import { useFocusFinders } from '@fluentui/react-tabster';
import React, { KeyboardEventHandler, PropsWithChildren, useEffect, useRef } from 'react';

export interface IPanelFocusTrapProps {
    /** When false, bumpers are non-tabbable and Tab is not trapped (nested modals / panel closed). */
    enabled: boolean;
    onKeyDownCapture?: KeyboardEventHandler<HTMLDivElement>;
}

const bumperStyle: React.CSSProperties = {
    pointerEvents: 'none',
    position: 'fixed',
};

/**
 * Non-Fluent-8 focus trap mirroring FocusTrapZone behavior needed by AddReturnPanel:
 * bumper tab stops, first focus on enable, Tab cycle, Escape via onKeyDownCapture.
 * Does not modalize/inert siblings (Syncfusion MultiSelect portals + overlay clicks must work).
 * Click-outside is allowed (former isClickableOutsideFocusTrap).
 */
const PanelFocusTrap: React.FC<PropsWithChildren<IPanelFocusTrapProps>> = ({ enabled, onKeyDownCapture, children }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const firstBumperRef = useRef<HTMLDivElement>(null);
    const lastBumperRef = useRef<HTMLDivElement>(null);
    const { findFirstFocusable, findLastFocusable } = useFocusFinders();

    useEffect(() => {
        if (!enabled || !rootRef.current) {
            return;
        }
        const first = findFirstFocusable(rootRef.current);
        first?.focus();
    }, [enabled, findFirstFocusable]);

    const focusBumper = (isFirstBumper: boolean) => {
        const root = rootRef.current;
        if (!enabled || !root) {
            return;
        }
        const target = isFirstBumper ? findLastFocusable(root) : findFirstFocusable(root);
        target?.focus();
    };

    const onRootFocusCapture: React.FocusEventHandler<HTMLDivElement> = (event) => {
        if (event.target === firstBumperRef.current) {
            focusBumper(true);
        } else if (event.target === lastBumperRef.current) {
            focusBumper(false);
        }
    };

    return (
        <div ref={rootRef} onKeyDownCapture={onKeyDownCapture} onFocusCapture={onRootFocusCapture}>
            <div
                ref={firstBumperRef}
                aria-hidden={true}
                style={bumperStyle}
                tabIndex={enabled ? 0 : -1}
                data-is-visible={true}
                data-is-focus-trap-zone-bumper={true}
            />
            {children}
            <div
                ref={lastBumperRef}
                aria-hidden={true}
                style={bumperStyle}
                tabIndex={enabled ? 0 : -1}
                data-is-visible={true}
                data-is-focus-trap-zone-bumper={true}
            />
        </div>
    );
};

export default PanelFocusTrap;
